import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import { useLazyAutocompleteAddressQuery } from '@/api/v1';
import { AddressAutocompleteSuggestion } from '@/types/addressValidation';
import { CountryType } from '@/types/country';
import { isCountryType } from '@/types/country.ext';

export const useAddressSearch = (
  country: string,
  address: string,
): [readonly AddressAutocompleteSuggestion[], boolean] => {
  const [validateAddress, validateAddressStatus] = useLazyAutocompleteAddressQuery();

  const [results, setResults] = React.useState<readonly AddressAutocompleteSuggestion[]>([]);

  const triggerValidateAddress = React.useCallback(async (): Promise<void> => {
    if (!isCountryType(country) || country === CountryType.unset || address.trim() === '') {
      setResults([]);
      return;
    }

    const { data: { suggestions } = { suggestions: [] } } = await validateAddress({
      country: country,
      address: address,
    });

    setResults(suggestions);
  }, [country, address, validateAddress]);

  React.useEffect((): VoidFunction => {
    const timeout = setTimeout(triggerValidateAddress, 800);

    return (): void => {
      clearTimeout(timeout);
    };
  }, [triggerValidateAddress]);

  return [results, validateAddressStatus.status === QueryStatus.pending];
};
